/**
 * Filtri avanzati v2: cataloghi operatori, helper payload e conversione
 * dei filtri salvati legacy (payload piatto) nel formato a gruppi.
 *
 * Payload v2: { version: 2, logic: 'and'|'or', groups: [{ logic, conditions: [{ field, operator, value }] }] }
 */

// Operatori senza valore
export const NO_VALUE_OPERATORS = [
	'is_empty', 'is_not_empty', 'is_true', 'is_false',
	'today', 'yesterday', 'this_week', 'this_month', 'last_month', 'this_year'
]

// Operatori con valore numerico "N giorni"
export const N_DAYS_OPERATORS = ['last_n_days', 'next_n_days']

// Operatori con intervallo {from, to}
export const RANGE_OPERATORS = ['between']

// Operatori con valore multiplo (array)
export const MULTI_VALUE_OPERATORS = ['in', 'not_in', 'is_any_of', 'contains_any', 'contains_all', 'contains_none']

const EMPTY_OPS = [
	{ value: 'is_empty', text: 'è vuoto' },
	{ value: 'is_not_empty', text: 'non è vuoto' }
]

export const OPERATORS_BY_TYPE = {
	text: [
		{ value: 'contains', text: 'contiene' },
		{ value: 'not_contains', text: 'non contiene' },
		{ value: 'equals', text: 'uguale a' },
		{ value: 'not_equals', text: 'diverso da' },
		{ value: 'starts_with', text: 'inizia con' },
		{ value: 'ends_with', text: 'finisce con' },
		{ value: 'is_any_of', text: 'è uno di' },
		{ value: 'contains_any', text: 'contiene uno di' },
		{ value: 'contains_all', text: 'contiene tutti' },
		{ value: 'contains_none', text: 'non contiene nessuno di' },
		...EMPTY_OPS
	],
	number: [
		{ value: 'equals', text: 'uguale a' },
		{ value: 'not_equals', text: 'diverso da' },
		{ value: 'gt', text: 'maggiore di' },
		{ value: 'gte', text: 'maggiore o uguale a' },
		{ value: 'lt', text: 'minore di' },
		{ value: 'lte', text: 'minore o uguale a' },
		{ value: 'between', text: 'compreso tra' },
		...EMPTY_OPS
	],
	date: [
		{ value: 'on', text: 'il giorno' },
		{ value: 'before', text: 'prima del' },
		{ value: 'after', text: 'dopo il' },
		{ value: 'between', text: 'compreso tra' },
		{ value: 'today', text: 'oggi' },
		{ value: 'yesterday', text: 'ieri' },
		{ value: 'this_week', text: 'questa settimana' },
		{ value: 'this_month', text: 'questo mese' },
		{ value: 'last_month', text: 'il mese scorso' },
		{ value: 'this_year', text: 'quest\'anno' },
		{ value: 'last_n_days', text: 'negli ultimi N giorni' },
		{ value: 'next_n_days', text: 'nei prossimi N giorni' },
		...EMPTY_OPS
	],
	time: [
		{ value: 'equals', text: 'alle' },
		{ value: 'not_equals', text: 'diverso da' },
		{ value: 'before', text: 'prima delle' },
		{ value: 'after', text: 'dopo le' },
		{ value: 'between', text: 'compreso tra' },
		...EMPTY_OPS
	],
	boolean: [
		{ value: 'is_true', text: 'sì' },
		{ value: 'is_false', text: 'no' }
	],
	relation: [
		{ value: 'in', text: 'è uno di' },
		{ value: 'not_in', text: 'non è nessuno di' },
		...EMPTY_OPS
	],
	chips: [
		{ value: 'contains_any', text: 'contiene uno di' },
		{ value: 'contains_all', text: 'contiene tutti' },
		{ value: 'contains_none', text: 'non contiene nessuno di' },
		...EMPTY_OPS
	]
}

// Tipo "logico" del builder a partire dal tipo del filtro backend
export function builderType(filterDef) {
	if (!filterDef) return 'text'
	switch (filterDef.type) {
		case 'number':
			return 'number'
		case 'date':
		case 'datetime':
			return 'date'
		case 'time':
			return 'time'
		case 'boolean':
		case 'null':
			return 'boolean'
		case 'belongsTo':
		case 'generalTypes':
		case 'morphTo':
		case 'belongsToMany':
		case 'belongsToThrough':
			return 'relation'
		case 'chips':
			return 'chips'
		default:
			return 'text'
	}
}

export function operatorsFor(filterDef) {
	const type = builderType(filterDef)
	if (type === 'boolean' && filterDef.type === 'null') {
		// I filtri legacy di tipo 'null' sono check di vuoto/non vuoto
		return EMPTY_OPS
	}
	return OPERATORS_BY_TYPE[type] || OPERATORS_BY_TYPE.text
}

export function defaultOperator(filterDef) {
	const type = builderType(filterDef)
	switch (type) {
		case 'text': return 'contains'
		case 'number': return 'equals'
		case 'date': return 'on'
		case 'time': return 'between'
		case 'boolean': return filterDef.type === 'null' ? 'is_empty' : 'is_true'
		case 'relation': return 'in'
		case 'chips': return 'contains_any'
		default: return 'contains'
	}
}

export function emptyCondition() {
	return { field: null, operator: null, value: null }
}

export function emptyGroup() {
	return { logic: 'and', conditions: [emptyCondition()] }
}

export function emptyPayload() {
	return { version: 2, logic: 'and', groups: [emptyGroup()] }
}

export function isV2Payload(value) {
	return !!value && typeof value === 'object' && Array.isArray(value.groups)
}

// Una condizione è completa (inviabile al backend)?
export function isConditionComplete(condition) {
	if (!condition || !condition.field || !condition.operator) return false
	if (NO_VALUE_OPERATORS.includes(condition.operator)) return true
	if (RANGE_OPERATORS.includes(condition.operator)) {
		const v = condition.value || {}
		return (v.from !== null && v.from !== undefined && v.from !== '') ||
			(v.to !== null && v.to !== undefined && v.to !== '')
	}
	const v = condition.value
	if (Array.isArray(v)) return v.length > 0
	return v !== null && v !== undefined && v !== ''
}

// Estrae dal payload builder solo i gruppi/condizioni completi
export function cleanPayload(payload) {
	if (!isV2Payload(payload)) return null
	const groups = payload.groups
		.map((group) => ({
			logic: group.logic === 'or' ? 'or' : 'and',
			conditions: (group.conditions || []).filter(isConditionComplete)
				.map((c) => {
					const out = { field: c.field, operator: c.operator }
					if (!NO_VALUE_OPERATORS.includes(c.operator)) {
						out.value = c.value
					}
					return out
				})
		}))
		.filter((group) => group.conditions.length > 0)

	if (!groups.length) return null

	return { version: 2, logic: payload.logic === 'or' ? 'or' : 'and', groups }
}

export function countConditions(payload) {
	if (!isV2Payload(payload)) return 0
	return payload.groups.reduce((sum, g) => sum + (g.conditions || []).filter(isConditionComplete).length, 0)
}

/**
 * Converte un filtro salvato legacy (payload piatto {and_or, not, key: value})
 * nel payload v2 a gruppo singolo. La negazione globale legacy viene tradotta
 * in operatori negati dove possibile.
 */
export function convertLegacyToV2(flatValue, availableFilters) {
	if (!flatValue || typeof flatValue !== 'object') return emptyPayload()
	if (isV2Payload(flatValue)) return normalizeV2(flatValue)

	const not = !!flatValue.not
	const andOr = flatValue.and_or === undefined ? true : !!flatValue.and_or
	const conditions = []

	// Un estremo di range è vuoto solo se null/undefined/'' (0 è un valore valido)
	const emptySide = (v) => v === null || v === undefined || v === ''

	_.forEach(flatValue, (value, key) => {
		if (key === 'and_or' || key === 'not') return
		if (value === null || value === undefined || value === '') return
		if (Array.isArray(value) && !value.length) return
		if (typeof value === 'object' && !Array.isArray(value) && emptySide(value.from) && emptySide(value.to)) return

		const def = _.find(availableFilters, (f) => f.key === key)
		const type = builderType(def)

		let operator = null
		let converted = value

		switch (type) {
			case 'text':
				operator = not ? 'not_contains' : 'contains'
				break
			case 'number':
			case 'date':
			// La fascia oraria arriva come {from, to} esattamente come date e numeri
			case 'time':
				operator = 'between'
				converted = {
					from: emptySide(value.from) ? null : value.from,
					to: emptySide(value.to) ? null : value.to
				}
				break
			case 'boolean':
				if (def && def.type === 'null') {
					operator = (not ? !value : !!value) ? 'is_not_empty' : 'is_empty'
				} else {
					operator = (not ? !value : !!value) ? 'is_true' : 'is_false'
				}
				converted = null
				break
			case 'relation':
				operator = not ? 'not_in' : 'in'
				converted = Array.isArray(value) ? value : [value]
				break
			case 'chips':
				operator = not ? 'contains_none' : 'contains_any'
				converted = Array.isArray(value) ? value : [value]
				break
			default:
				operator = not ? 'not_contains' : 'contains'
		}

		if (!operator) return

		const condition = { field: key, operator }
		if (!NO_VALUE_OPERATORS.includes(operator)) {
			condition.value = converted
		} else {
			condition.value = null
		}
		conditions.push(condition)
	})

	if (!conditions.length) return emptyPayload()

	return {
		version: 2,
		logic: 'and',
		groups: [{ logic: andOr ? 'and' : 'or', conditions }]
	}
}

// Normalizza un payload v2 caricato (filtri salvati) nello stato builder
export function normalizeV2(payload) {
	const groups = (payload.groups || []).map((g) => ({
		logic: g.logic === 'or' ? 'or' : 'and',
		conditions: (g.conditions || []).map((c) => ({
			field: c.field || null,
			operator: c.operator || null,
			value: c.value === undefined ? null : c.value
		}))
	}))

	return {
		version: 2,
		logic: payload.logic === 'or' ? 'or' : 'and',
		groups: groups.length ? groups : [emptyGroup()]
	}
}

// URL per caricare i campi filtrabili del correlato di una relazione drillabile
// (filtro con relation e relation_model). Solo per le n-n si passano parent e
// relation, che servono al backend per aggiungere i campi della riga (pivot);
// per le altre la richiesta resta per modello, come prima
export function relatedFieldsUrl(rel, modelDef) {
	const url = 'filters-fields/' + rel.relation_model
	if (rel.type !== 'belongsToMany') return url
	return url + '?parent=' + encodeURIComponent(modelDef) + '&relation=' + encodeURIComponent(rel.relation)
}

// Chiave della cache dei campi del correlato: per le n-n i campi della riga
// dipendono dal modello padre (modelDef|relazione), per le altre basta il modello
export function relatedFieldsCacheKey(rel, modelDef) {
	return rel.type === 'belongsToMany' ? modelDef + '|' + rel.relation : rel.relation_model
}

// Vero se il payload ha condizioni su relazioni n-n: filtro relazione di tipo
// belongsToMany oppure campo del correlato/della riga ("R.campo") con R n-n.
// Sono le condizioni a cui si applica la regola della stessa riga
export function hasManyToManyConditions(payload, availableFilters) {
	const manyToMany = (availableFilters || []).filter(f =>
		f.type === 'belongsToMany' && typeof f.relation === 'string' && f.relation
	)
	const keys = manyToMany.map(f => f.key)
	const relations = manyToMany.map(f => f.relation)

	return ((payload && payload.groups) || []).some(g => (g.conditions || []).some(c => {
		const field = typeof c.field === 'string' ? c.field : ''
		return field !== '' && (
			keys.includes(field) ||
			(field.indexOf('.') !== -1 && relations.includes(field.split('.')[0]))
		)
	}))
}

// Voci della lista campi per una relazione drillabile: prima i campi del
// correlato, poi (se presenti) quelli della riga della relazione (group 'pivot')
export function drillFieldItems(rel, fields) {
	const items = []
	const related = (fields || []).filter(f => f.group !== 'pivot')
	const pivot = (fields || []).filter(f => f.group === 'pivot')

	if (related.length) {
		items.push({ header: rel.label })
		related.forEach(f => {
			items.push(Object.assign({}, f, { key: rel.relation + '.' + f.key, label: rel.label + ' → ' + f.label }))
		})
	}
	if (pivot.length) {
		items.push({ header: rel.label + ' (riga)' })
		pivot.forEach(f => {
			items.push(Object.assign({}, f, { key: rel.relation + '.' + f.key, label: rel.label + ' (riga) → ' + f.label }))
		})
	}
	return items
}
